// NotificationSettingsPage — the Notifications console: every email
// BidLight sends, what triggers it, recent volume, and a global on/off
// switch per source.
//
// Data: GET /notification-settings            → { settings: [...], canEdit }
//       PUT /notification-settings/:source    → { source, enabled } (403 for non-admins)
//
// Toggles are optimistic: flip locally, call the PUT, revert + message.error
// on failure. Rows are grouped Scheduled → Event-driven → Manual based on
// the `when` text.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Card, Modal, Skeleton, Switch, Table, Tag, Tooltip, message, Select, Button, Checkbox } from "antd";
import usePermissions from "hooks/usePermissions";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import useLastProject from "components/Coordination/useLastProject";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import { T } from "pages/Issue/theme";
import { usePartnerTrades } from "components/Partners/partnerTrades";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import MailRolloutCard from "./MailRolloutCard";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

async function fetchSettings() {
  const res = await fetch(`${url}/notification-settings`, { headers: auth() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function putSetting(source, enabled) {
  const res = await fetch(
    `${url}/notification-settings/${encodeURIComponent(source)}`,
    {
      method: "PUT",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    }
  );
  if (!res.ok) {
    if (res.status === 403) throw new Error("Only admins can change notification switches.");
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.err || body?.message || msg;
    } catch (e) { /* keep the status text */ }
    throw new Error(msg);
  }
  return res.json();
}

// Scheduled first, then event-driven, then manual.
const GROUPS = [
  { key: "scheduled", title: "Scheduled", hint: "Sent on a fixed clock — digests, reminders, nightly jobs." },
  { key: "event", title: "Event-driven", hint: "Sent the moment something happens in a project." },
  { key: "manual", title: "Manual", hint: "Sent only when a person explicitly triggers them." },
  { key: "platform", title: "Platform (super admin)", hint: "Account, licence, billing, invites, contact form, demo and worker alerts - only the super admin sees and controls these." },
];

function groupOf(when, scope) {
  if (scope === "platform") return "platform";
  const w = String(when || "").toLowerCase();
  // "1st of the month" is a clock, not an event - without `month` the
  // monthly report was filed under Event-driven.
  if (/daily|monday|nightly|month/.test(w)) return "scheduled";
  if (/manual/.test(w)) return "manual";
  return "event";
}

function lastSentText(v) {
  if (!v) return "—";
  const m = moment(v);
  return m.isValid() ? m.fromNow() : "—";
}

function lastSentTitle(v) {
  if (!v) return "";
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : "";
}

export default function NotificationSettingsPage() {
  const userInfoRedux = useSelector((s) => s.user?.userInfo);
  const isAdminUser = !!(userInfoRedux?.admin || userInfoRedux?.companyAdmin);
  const { hasAny } = usePermissions();
  const canManageProjects = hasAny(ALL_PERMISSIONS.NOTIFICATIONS_MANAGE, ALL_PERMISSIONS.PARTICIPANT_MANAGE);
  const [settings, setSettings] = useState([]);
  const [delivery, setDelivery] = useState(null); // {timezone, effectiveTimezone, digestHour, options, projects}
  const [savingDelivery, setSavingDelivery] = useState(false);
  // Per-project recipients (internal team / partners / their people).
  const [rcpProject, setRcpProject] = useLastProject(delivery?.projects);
  // Each partner group headed by the company with its trades beneath (owner, 2026-10-16).
  const rcpTrades = usePartnerTrades(rcpProject || null);
  const [rcp, setRcp] = useState(null);
  const [rcpLoading, setRcpLoading] = useState(false);
  useEffect(() => {
    if (!rcpProject) { setRcp(null); return undefined; }
    let cancelled = false;
    setRcpLoading(true);
    fetch(`${url}/notification-settings/recipients/${rcpProject}`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b?.err || `HTTP ${r.status}`); return b; })
      .then((b) => { if (!cancelled) setRcp(b); })
      .catch((e) => { if (!cancelled) { setRcp(null); message.error(e.message || "Could not load recipients"); } })
      .finally(() => { if (!cancelled) setRcpLoading(false); });
    return () => { cancelled = true; };
  }, [rcpProject]);
  // `optIn` is optional: the mute-only callers pass one argument and behave
  // exactly as before. When it is given, both halves are written in ONE request -
  // ticking somebody also un-mutes them, and those must not be two round trips
  // that can half-fail and leave the screen disagreeing with the gate.
  const saveRcp = async (next, optIn) => {
    const prev = rcp;
    setRcp((r) => ({ ...r, disabled: next, ...(optIn ? { optIn } : {}) }));
    try {
      const res = await fetch(`${url}/notification-settings/recipients/${rcpProject}`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          disabledCompanyIds: next.companyIds, disabledUserIds: next.userIds, disabledEmails: next.emails,
          ...(optIn ? { optInRequired: !!optIn.required, enabledUserIds: optIn.userIds, enabledEmails: optIn.emails } : {}),
        }),
      });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b?.err || `HTTP ${res.status}`);
    } catch (e) {
      setRcp(prev);
      message.error(e.message || "Could not save recipients");
    }
  };
  const toggleIn = (arr, v, on) => (on ? arr.filter((x) => x !== v) : [...new Set([...arr, v])]);
  const [canEdit, setCanEdit] = useState(false);
  // Super admin: the per-company mail rollout switches (owner brief 2026-10-11 T33).
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // 'company' (company admin: everything) or 'projects' (a member with the
  // permission: who gets the reports on the projects they run, only).
  const [scope, setScope] = useState("company");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({}); // source → bool

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await fetchSettings();
      setSettings(Array.isArray(body?.settings) ? body.settings : []);
      setCanEdit(!!body?.canEdit);
      setIsSuperAdmin(!!body?.isSuperAdmin);
      setScope(body?.scope === "projects" ? "projects" : "company");
      setDelivery(body?.delivery || null);
    } catch (e) {
      setError(e.message || "Failed to load notification settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (source, enabled) => {
    // Optimistic flip; revert on failure.
    setSettings((prev) =>
      prev.map((s) => (s.source === source ? { ...s, enabled } : s))
    );
    setSaving((prev) => ({ ...prev, [source]: true }));
    try {
      await putSetting(source, enabled);
    } catch (e) {
      setSettings((prev) =>
        prev.map((s) => (s.source === source ? { ...s, enabled: !enabled } : s))
      );
      message.error(e.message || "Could not update the switch.");
    } finally {
      setSaving((prev) => ({ ...prev, [source]: false }));
    }
  };

  const saveDelivery = async (patch) => {
    setSavingDelivery(true);
    try {
      const res = await fetch(`${url}/notification-settings/delivery`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify(patch),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.err || `HTTP ${res.status}`);
      setDelivery((d) => ({ ...d, timezone: body.timezone, effectiveTimezone: body.timezone || d?.effectiveTimezone, digestHour: body.digestHour, skipWeekends: body.skipWeekends }));
      message.success("Delivery settings saved");
    } catch (e) {
      message.error(e.message || "Could not save delivery time");
    } finally {
      setSavingDelivery(false);
    }
  };
  const saveProjectTz = async (projectId, timezone) => {
    try {
      const res = await fetch(`${url}/notification-settings/project/${projectId}/timezone`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify({ timezone }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.err || `HTTP ${res.status}`);
      setDelivery((d) => ({ ...d, projects: (d?.projects || []).map((p) => (String(p._id) === String(projectId) ? { ...p, timezone } : p)) }));
      message.success("Project time zone saved");
    } catch (e) {
      message.error(e.message || "Could not save project time zone");
    }
  };
  const tzOptions = (delivery?.options || []).map((z) => ({ value: z, label: z.replace(/_/g, " ") }));
  const hourOptions = Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, "0")}:00` }));
  const localNow = (tz) => { try { return new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "2-digit", minute: "2-digit", weekday: "short" }).format(new Date()); } catch { return ""; } };

  const columns = [
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      render: (enabled, row) => (
        <Switch
          size="small"
          checked={!!enabled}
          disabled={!canEdit}
          loading={!!saving[row.source]}
          onChange={(next) => toggle(row.source, next)}
        />
      ),
    },
    {
      title: "Notification",
      dataIndex: "label",
      key: "label",
      render: (label, row) => (
        <div>
          <div style={{ fontWeight: 600, color: T.ink }}>{label || row.source}</div>
          {row.desc ? (
            <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>{row.desc}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: "When",
      dataIndex: "when",
      key: "when",
      width: 160,
      render: (when) =>
        when ? (
          <Tag
            style={{
              background: T.wash,
              borderColor: "#D4E3EC",
              color: T.navyD,
              marginRight: 0,
            }}
          >
            {when}
          </Tag>
        ) : (
          <span style={{ color: T.sub }}>—</span>
        ),
    },
    {
      title: "Sent last 7 days",
      dataIndex: "sent7d",
      key: "sent7d",
      width: 130,
      align: "right",
      render: (n) => {
        const v = Number(n) || 0;
        return (
          <span style={{ color: v ? T.text : T.sub, fontVariantNumeric: "tabular-nums" }}>
            {v}
          </span>
        );
      },
    },
    {
      title: "Last sent",
      dataIndex: "lastSent",
      key: "lastSent",
      width: 150,
      render: (v) => (
        <Tooltip title={lastSentTitle(v)}>
          <span style={{ color: v ? T.text : T.sub }}>{lastSentText(v)}</span>
        </Tooltip>
      ),
    },
  ];

  const grouped = GROUPS.map((g) => ({
    ...g,
    rows: settings.filter((s) => groupOf(s.when, s.scope) === g.key),
  })).filter((g) => g.rows.length > 0);

  if (userInfoRedux && !isAdminUser && !canManageProjects) return <Redirect to="/coordination" />;
  const projectScope = scope === "projects";
  return (
    <div style={{ background: T.bg, minHeight: "calc(100 * var(--vh))", padding: "24px 24px 48px" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <CoordinationBreadcrumbs current="Reports & notifications" />

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 18,
          }}
        >
          <div>
            <h2 style={{ color: T.ink, fontWeight: 700, marginBottom: 4 }}>
              Notifications
            </h2>
            <div style={{ color: T.muted }}>
              {projectScope
                ? "Who gets the reports on the projects you run. Company-wide switches are for company admins."
                : "Every email BidLight sends — what, when, and a switch for each for your company."}
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              border: `1px solid ${T.line2}`,
              background: "#fff",
              color: T.text,
              borderRadius: 6,
              padding: "5px 12px",
              cursor: loading ? "default" : "pointer",
            }}
          >
            <ReloadOutlined spin={loading} /> Refresh
          </button>
        </div>

        {!canEdit && !projectScope && !loading && !error ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Only admins can change these switches — you're viewing read-only."
          />
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Could not load notification settings"
            description={error}
          />
        ) : null}

        {isSuperAdmin && !projectScope && <MailRolloutCard />}

        {delivery && !projectScope && (
          <Card style={{ borderColor: T.line, marginBottom: 16 }} size="small"
                title={<span style={{ fontWeight: 700, color: T.ink }}>Delivery time &amp; working days (team location)</span>}>
            <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 10 }}>
              Daily digests and the Monday weekly report go out at this local hour in your team's time zone; executive copies
              three hours later. Nothing is sent on a Saturday or Sunday, and nothing old is sent: a report missed on its day
              is not sent on a later day, and Monday's digest reports the current state rather than replaying the weekend.
              Partner supervisors get theirs in their own company's zone when they set one, otherwise the project's.
              Currently {localNow(delivery.effectiveTimezone)} in {delivery.effectiveTimezone.replace(/_/g, " ")}.
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: T.muted }}>Time zone</span>
              <Select showSearch style={{ minWidth: 260 }} value={delivery.timezone || delivery.effectiveTimezone}
                      options={tzOptions} disabled={!canEdit || savingDelivery}
                      filterOption={(i, o) => String(o?.label || "").toLowerCase().includes(i.toLowerCase())}
                      onChange={(v) => saveDelivery({ timezone: v })} />
              <span style={{ fontSize: 12, color: T.muted }}>Send at</span>
              <Select style={{ width: 100 }} value={delivery.digestHour} options={hourOptions}
                      disabled={!canEdit || savingDelivery} onChange={(v) => saveDelivery({ digestHour: v })} />
              {delivery.timezone && canEdit && (
                <Button size="small" onClick={() => saveDelivery({ timezone: "" })}>Use default ({"America/New York"})</Button>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, color: T.ink }}>No email on Saturday or Sunday</span>
              <span style={{ fontSize: 12, color: T.muted }}>
                Digests and reminders skip the weekend. A monthly report due on a weekend goes out on Monday. Mail raised at the
                weekend (an issue assigned to you, a comment, a sign-off) is held and delivered on Monday at 06:00 local time.
                Sign-in links and password resets are sent at once, any day. This applies to every company and cannot be turned off.
              </span>
            </div>
            {(delivery.projects || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>
                  Project location overrides (leave empty to follow the company zone):
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 8 }}>
                  {delivery.projects.map((p) => (
                    <div key={p._id} style={{ display: "flex", alignItems: "center", gap: 8, border: `1px solid ${T.line}`, borderRadius: 6, padding: "4px 8px" }}>
                      <span style={{ flex: 1, fontSize: 12.5, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.title}>{p.title}</span>
                      <Select size="small" showSearch allowClear placeholder="company zone" style={{ width: 190 }}
                              value={p.timezone || undefined} options={tzOptions} disabled={!canEdit}
                              filterOption={(i, o) => String(o?.label || "").toLowerCase().includes(i.toLowerCase())}
                              onChange={(v) => saveProjectTz(p._id, v || "")} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {delivery && (
          <Card style={{ borderColor: T.line, marginBottom: 16 }} size="small"
                title={<span style={{ fontWeight: 700, color: T.ink }}>Who gets the reports (per project)</span>}
                extra={
                  <Select showSearch style={{ minWidth: 260 }} placeholder="Pick a project" value={rcpProject || undefined}
                          options={(delivery.projects || []).map((p) => ({ value: String(p._id), label: p.title }))}
                          filterOption={(i, o) => String(o?.label || "").toLowerCase().includes(i.toLowerCase())}
                          onChange={(v) => setRcpProject(v || "")} />
                }>
            <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 10 }}>
              Ticked people receive the scheduled reports for this project (daily focus, partner supervisor digest, weekly coordination report, owner reminders). Untick a whole partner company or single people. Everyone is on by default; changes save immediately.
            </div>
            {!rcpProject ? <div style={{ color: T.muted, fontSize: 12.5 }}>Choose a project above.</div>
              : rcpLoading ? <Skeleton active paragraph={{ rows: 3 }} />
              : !rcp ? null : (() => {
                const d = rcp.disabled || { companyIds: [], userIds: [], emails: [] };
                const oi = rcp.optIn || { required: false, userIds: [], emails: [] };
                const optIn = !!oi.required;
                const canRcp = !!rcp.canEdit;
                // IN OPT-IN MODE A TICK MEANS "ticked to receive", and a mute still
                // outranks it on the server - so the box shows on ONLY when both
                // agree. Anything else would put a tick on screen next to somebody
                // the gate refuses.
                const userOn = (uid) => (optIn
                  ? oi.userIds.includes(String(uid)) && !d.userIds.includes(String(uid))
                  : !d.userIds.includes(String(uid)));
                const emailOn = (em) => (optIn
                  ? oi.emails.includes(String(em).toLowerCase()) && !d.emails.includes(String(em).toLowerCase())
                  : !d.emails.includes(String(em).toLowerCase()));
                const compOn = (cid) => !d.companyIds.includes(String(cid));
                // Ticking in opt-in mode ADDS to the enabled list and REMOVES any
                // mute, in one write: a tick that left a mute behind would look
                // like it worked and send nothing.
                const setUser = (uid, on) => (optIn
                  ? saveRcp({ ...d, userIds: d.userIds.filter((x) => x !== String(uid)) },
                    { ...oi, userIds: on ? [...new Set([...oi.userIds, String(uid)])] : oi.userIds.filter((x) => x !== String(uid)) })
                  : saveRcp({ ...d, userIds: toggleIn(d.userIds, String(uid), on) }));
                const setEmail = (em, on) => { const e = String(em).toLowerCase(); return optIn
                  ? saveRcp({ ...d, emails: d.emails.filter((x) => x !== e) },
                    { ...oi, emails: on ? [...new Set([...oi.emails, e])] : oi.emails.filter((x) => x !== e) })
                  : saveRcp({ ...d, emails: toggleIn(d.emails, e, on) }); };
                const Person = ({ label, on, disabled, onChange, sub }) => (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: disabled ? T.sub : T.text, cursor: disabled ? "default" : "pointer" }}>
                    <Checkbox checked={on} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
                    <span>{label}{sub ? <span style={{ color: T.muted }}> - {sub}</span> : null}</span>
                  </label>
                );
                const Group = ({ title, color, children, headerRight }) => (
                  <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: "8px 10px", minWidth: 260, flex: "1 1 300px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      {color ? <span style={{ width: 10, height: 10, borderRadius: 2, background: color }} /> : null}
                      <span style={{ fontWeight: 700, color: T.ink, fontSize: 13, flex: 1 }}>{title}</span>
                      {headerRight}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
                  </div>
                );
                return (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ width: "100%", border: `1px solid ${optIn ? "#FFB300" : T.line}`, background: optIn ? "#FFFDF5" : "transparent", borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                      <Switch size="small" checked={optIn} disabled={!canRcp}
                              onChange={(on) => {
                                // TURNING IT ON SILENCES PEOPLE. Say how many, at the
                                // moment of the act, and count them the same way the
                                // gate does - ticked, and not muted. A switch that
                                // quietly stopped somebody's mail would be the same
                                // silent degradation this feature exists to end.
                                if (!on) { saveRcp(d, { ...oi, required: false }); return; }
                                const people = [
                                  ...(rcp.internal?.users || []).map((u) => ({ id: String(u.userId), em: "" })),
                                  ...(rcp.partners || []).flatMap((p) => [
                                    ...(p.supervisors || []).map((sv) => ({ id: "", em: String(sv.email || "").toLowerCase() })),
                                    ...(p.users || []).map((u) => ({ id: u.userId ? String(u.userId) : "", em: String(u.email || "").toLowerCase() })),
                                  ]),
                                ];
                                const receiving = people.filter((x) => (x.id ? !d.userIds.includes(x.id) : !d.emails.includes(x.em)));
                                const ticked = people.filter((x) => (x.id ? oi.userIds.includes(x.id) : oi.emails.includes(x.em)));
                                const stopping = receiving.length - ticked.length;
                                Modal.confirm({
                                  title: "Only ticked people receive email?",
                                  content: stopping > 0
                                    ? `${receiving.length} ${receiving.length === 1 ? "person receives" : "people receive"} email on this project today and ${ticked.length} ${ticked.length === 1 ? "is" : "are"} ticked, so ${stopping} ${stopping === 1 ? "person stops" : "people stop"} receiving until you tick them. Nothing is sent to them in the meantime.`
                                    : "Nobody on this project loses anything - every person receiving today is already ticked.",
                                  okText: "Turn it on", cancelText: "Cancel",
                                  onOk: () => saveRcp(d, { ...oi, required: true }),
                                });
                              }} />
                      <div style={{ fontSize: 12.5, color: T.text }}>
                        <b>Only ticked people receive email on this project</b>
                        <div style={{ color: T.muted }}>
                          {optIn
                            ? `On: nobody gets a digest, report or scheduled notice unless their box below is ticked - ${(oi.userIds || []).length + (oi.emails || []).length} ticked so far.`
                            : "Off: everyone on the project receives, and the boxes below mute the exceptions."}
                        </div>
                      </div>
                    </div>
                    <Group title={`Internal team (${rcp.internal?.companyName || "BidLight"})`}>
                      {(rcp.internal?.users || []).length === 0 && <span style={{ color: T.muted, fontSize: 12 }}>No internal users on this project.</span>}
                      {(rcp.internal?.users || []).map((u) => (
                        <Person key={u.userId} label={u.name} sub={u.email} on={userOn(u.userId)} disabled={!canRcp}
                                onChange={(on) => setUser(u.userId, on)} />
                      ))}
                    </Group>
                    {(rcp.partners || []).map((p) => {
                      const cOn = compOn(p.companyId);
                      return (
                        <Group key={p.companyId} title={<PartnerWithTrades name={p.companyName} trades={rcpTrades.tradesOf(p.companyId)} />} color={p.color}
                               headerRight={<Checkbox checked={cOn} disabled={!canRcp} onChange={(e) => saveRcp({ ...d, companyIds: toggleIn(d.companyIds, String(p.companyId), e.target.checked) })}>whole company</Checkbox>}>
                          {(p.supervisors || []).map((sv) => (
                            <Person key={`s-${sv.email}`} label={`${sv.name} (supervisor)`} sub={sv.email} on={cOn && emailOn(sv.email)} disabled={!canRcp || !cOn}
                                    onChange={(on) => setEmail(sv.email, on)} />
                          ))}
                          {(p.users || []).map((u) => (
                            <Person key={u.userId || `c-${u.email}`} label={u.name} sub={`${u.email}${u.kind === "contact" ? " (no account)" : ""}`}
                                    on={cOn && (u.userId ? userOn(u.userId) : emailOn(u.email))} disabled={!canRcp || !cOn}
                                    onChange={(on) => (u.userId ? setUser(u.userId, on) : setEmail(u.email, on))} />
                          ))}
                          {(p.users || []).length === 0 && (p.supervisors || []).length === 0 && <span style={{ color: T.muted, fontSize: 12 }}>No people listed yet - add them under Partners.</span>}
                        </Group>
                      );
                    })}
                    {(rcp.partners || []).length === 0 && <div style={{ color: T.muted, fontSize: 12.5 }}>No partner companies on this project yet.</div>}
                  </div>
                );
              })()}
          </Card>
        )}

        {loading ? (
          <Card style={{ borderColor: T.line }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        ) : projectScope ? null : (
          grouped.map((g) => (
            <div key={g.key} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
                <div
                  style={{
                    color: T.navyD,
                    fontWeight: 700,
                    fontSize: 15,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {g.title}
                </div>
                <div style={{ color: T.sub, fontSize: 12 }}>{g.hint}</div>
              </div>
              <Card
                bodyStyle={{ padding: 0 }}
                style={{ borderColor: T.line, overflow: "hidden" }}
              >
                <Table
                  rowKey="source"
                  columns={columns}
                  dataSource={g.rows}
                  pagination={false}
                  size="middle"
                />
              </Card>
            </div>
          ))
        )}

        {!loading && !error && grouped.length === 0 ? (
          <Card style={{ borderColor: T.line, textAlign: "center", color: T.muted }}>
            No notification sources configured.
          </Card>
        ) : null}
      </div>
    </div>
  );
}
